import { create } from 'zustand';
import type { MyProfile, PublicUser } from '../types/api';

// Session holds just-enough identity for render. Full profile (with avatarUrl)
// is cached separately via TanStack Query — see use-me.ts.
type AuthState = {
  session: PublicUser | null;
  hydrated: boolean;
  setSession: (s: PublicUser | MyProfile | null) => void;
  setHydrated: (v: boolean) => void;
  signOut: () => void;
};

function getSessionStorageKey(roleOrScope?: string): string {
  if (typeof window === 'undefined') return 'ecregi_client.session';
  if ((window as any).__ECREGI_APP_ROLE === 'admin' || roleOrScope === 'admin' || roleOrScope === 'ADMIN') {
    return 'ecregi_admin.session';
  }
  const isAssociate =
    roleOrScope === 'associate' ||
    roleOrScope === 'ASSOCIATE' ||
    roleOrScope === 'FRANCHISE' ||
    window.location.pathname.startsWith('/associate') ||
    window.location.search.includes('as=associate');
  return isAssociate ? 'ecregi_associate.session' : 'ecregi_client.session';
}

function getInitialSession(): PublicUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const key = getSessionStorageKey();
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const initialSession = getInitialSession();

export const useAuthStore = create<AuthState>((set) => ({
  session: initialSession,
  hydrated: !!initialSession,
  setSession: (s) => {
    const formatted = s
      ? {
          id: s.id,
          email: s.email,
          role: s.role,
          adminRole: s.adminRole ?? null,
          status: s.status,
          firstName: s.firstName,
          lastName: s.lastName,
          mobile: s.mobile,
          rejectionReason: s.rejectionReason ?? null,
          profilePictureS3Key: s.profilePictureS3Key,
        }
      : null;

    try {
      const key = getSessionStorageKey(s?.role);
      if (formatted) {
        sessionStorage.setItem(key, JSON.stringify(formatted));
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }

    set({ session: formatted, hydrated: true });
  },
  setHydrated: (v) => set({ hydrated: v }),
  signOut: () => {
    try {
      sessionStorage.removeItem(getSessionStorageKey());
    } catch {
      /* ignore */
    }
    set({ session: null });
  },
}));

